import { Core } from "@faire/design-tokens";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { History } from "history";
import * as React from "react";
import { useHistory } from "react-router-dom";

interface IProps {
  error: unknown;
}

enum ErrorCode {
  SUB_USER_IS_ALREADY_ACTIVE = "UserInvitationValidationException.ALREADY_CREATED_ACCOUNT",
}

interface ErrorDetails {
  ctaText?: string | undefined;
  ctaAction?: (() => void) | undefined;
}

const errorCodeToErrorDetailsMap = (
  strictLocalize: StrictLocalizeFunction,
  push: History["push"]
): Record<ErrorCode, ErrorDetails> => ({
  [ErrorCode.SUB_USER_IS_ALREADY_ACTIVE]: {
    ctaText: strictLocalize({
      defaultMessage: "Continue to Sign In",
    }),
    ctaAction: () => {
      push(home({ signIn: "1" }));
    },
  },
});

export const SubUserOnboardingError: React.FC<IProps> = ({ error }) => {
  const { strictLocalize } = useStrictLocalization();
  const { push } = useHistory();
  if (!(error instanceof WebApiError)) {
    return null;
  }

  const apiError = error.responseData;
  const errorDetails: ErrorDetails | undefined = errorCodeToErrorDetailsMap(
    strictLocalize,
    push
  )[(apiError.service_error_code ?? "") as ErrorCode];

  if (!apiError.localized_title && !apiError.localized_message) {
    return null;
  }

  return (
    <Column align="center">
      <Spacer size="10x" />
      <Typography variant="pageHeaderSerifRegular">
        {apiError?.localized_title}
      </Typography>
      <Spacer size="1x" />
      <Typography color={Core.text.critical} align="center">
        {apiError?.localized_message}
      </Typography>
      <Spacer size="3x" />
      {errorDetails?.ctaText && errorDetails?.ctaAction ? (
        <Button onClick={errorDetails.ctaAction}>{errorDetails.ctaText}</Button>
      ) : null}
    </Column>
  );
};
